import { useCallback, useState } from 'react';
import useFetch, { ServiceParams, ServiceResponse, refreshSession } from '../hooks/useFetch';
import EndPoints from '../config/EndPoint';
import { Service, ServiceEdit } from '../models/ServiceModel';

export type Events = Partial<{
  onSuccess: (r: ServiceResponse) => void;
  onError: (r: ServiceResponse) => void;
}>;

const qs = (filters?: Record<string, any>) => {
  if (!filters) return "";
  return Object.entries(filters)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join("&");
};

/** fetch con cookies; si 401, refresca sesión y reintenta una vez. */
const fetchWithAuth = async (url: string, init: RequestInit): Promise<Response> => {
  const withCreds: RequestInit = { ...init, credentials: 'include' };
  let response = await fetch(url, withCreds);
  if (response.status === 401) {
    const ok = await refreshSession();
    if (ok) {
      response = await fetch(url, withCreds);
    }
  }
  return response;
};


/**
 * useServicesService
 * - Controla creación de servicios paso a paso (step1 → step3 → store)
 * - Compatible con la arquitectura de tus otros módulos (Authorizers, Drivers, etc.)
 */
const useServicesService = () => {
  const { doGet, doPost, doPut, doDelete } = useFetch();
  const [fetching, setFetching] = useState<Record<string, boolean>>({});

  const setFlag = useCallback((key: string, val: boolean) => {
    setFetching(prev => ({ ...prev, [key]: val }));
  }, []);

  // 📍 STEP 1: Empresas disponibles
  const step1 = useCallback(
    (events: Events = {}) =>
      doGet({
        url: EndPoints.SERVICES.STEP1,
        setFetching: v => setFlag('step1', v),
        ...events,
      } as ServiceParams),
    [doGet, setFlag]
  );
  const getById = useCallback(
    (id: number, events: Events = {}) =>
      doGet({
        url: EndPoints.SERVICES.GET_BY_ID.replace(':id', String(id)),
        setFetching: v => setFlag('getById', v),
        ...events,
      } as ServiceParams),
    [doGet, setFlag]
  );
  // 📍 STEP 2: Pasajeros y direcciones según empresa
  const step2 = useCallback(
    (companyId: number, events: Events = {}, signal?: AbortSignal) =>
      doGet({
        url: EndPoints.SERVICES.STEP2.replace(':company_id', String(companyId)),
        setFetching: v => setFlag('step2', v),
        signal,
        ...events,
      } as ServiceParams),
    [doGet, setFlag]
  );
const update = useCallback(
  (id: number, payload: Partial<ServiceEdit>, events: Events = {}) =>
    doPut({
      url: EndPoints.SERVICES.UPDATE.replace(':id', String(id)),
      body: payload,
      setFetching: v => setFlag('update', v),
      ...events,
    } as ServiceParams),
  [doPut, setFlag]
);

/** Ruta del servicio: origen, destino y puntos adicionales (`stops`). */
const getRoute = useCallback(
  (id: number, events: Events = {}) =>
    doGet({
      url: EndPoints.SERVICES.UPDATE_ROUTE.replace(':id', String(id)),
      setFetching: v => setFlag('getRoute', v),
      ...events,
    } as ServiceParams),
  [doGet, setFlag]
);

const updateRoute = useCallback(
  (
    id: number,
    payload: { origin?: number; destination?: number; comment: string; invert?: boolean; stops?: number[] },
    events: Events = {}
  ) =>
    doPut({
      url: EndPoints.SERVICES.UPDATE_ROUTE.replace(':id', String(id)),
      body: payload,
      setFetching: v => setFlag('updateRoute', v),
      ...events,
    } as ServiceParams),
  [doPut, setFlag]
);

const updateStatus = useCallback(
  (id: number, status: string, events: Events = {}) =>
    doPut({
      url: EndPoints.SERVICES.UPDATE_STATUS.replace(':id', String(id)),
      body: { status },
      setFetching: v => setFlag('updateStatus', v),
      ...events,
    } as ServiceParams),
  [doPut, setFlag]
);

const assignDriver = useCallback(
  (id: number, driverId: number, events: Events = {}) =>
    doPut({
      url: EndPoints.SERVICES.ASSIGN_DRIVER.replace(':id', String(id)),
      body: { driver: driverId },
      setFetching: v => setFlag('assignDriver', v),
      ...events,
    } as ServiceParams),
  [doPut, setFlag]
);

const releaseDriver = useCallback(
  (id: number, comment: string, events: Events = {}) =>
    doPut({
      url: EndPoints.SERVICES.RELEASE_DRIVER.replace(':id', String(id)),
      body: { comment },
      setFetching: v => setFlag('releaseDriver', v),
      ...events,
    } as ServiceParams),
  [doPut, setFlag]
);

  const updateAdicional = useCallback(
    (
      id: number,
      payload: {
        management?: number | null;
        superintendency?: number | null;
        area?: number | null;
        service_reason?: number | null;
        /** Motivo libre (además o en lugar del catálogo `service_reason`). */
        service_reason_text?: string | null;
        /** ENAEX: tabla propia `service_enaex_additionals`. */
        code?: string;
      },
      events: Events = {}
    ) =>
      doPut({
        url: EndPoints.SERVICES.ADICIONAL_UPDATE.replace(':id', String(id)),
        body: payload,
        setFetching: v => setFlag('updateAdicional', v),
        ...events,
      } as ServiceParams),
    [doPut, setFlag]
  );

  // 📍 STEP 3: Conductores, autorizadores, solicitantes y centros de costo por empresa
  const step3 = useCallback(
    (companyId: number, events: Events = {}, signal?: AbortSignal) =>
      doGet({
        url: EndPoints.SERVICES.STEP3.replace(':company_id', String(companyId)),
        setFetching: v => setFlag('step3', v),
        signal,
        ...events,
      } as ServiceParams),
    [doGet, setFlag]
  );

  // 🧾 STORE: Crear servicio con secuencia
  const store = useCallback(
    (payload: Service, events: Events = {}) =>
      doPost({
        url: EndPoints.SERVICES.STORE,
        body: payload,
        setFetching: v => setFlag('store', v),
        ...events,
      } as ServiceParams),
    [doPost, setFlag]
  );

  // 🧾 LISTAR: Servicios existentes (si se usa tabla principal)
  const sameTimeServices = useCallback(
    (
      params: {
        company_id: number;
        service_date: string;
        service_time: string;
        exclude_service_id?: number;
        service_code?: string;
      },
      events: Events = {},
      signal?: AbortSignal
    ) =>
      doGet({
        url: `${EndPoints.SERVICES.SAME_TIME}?${qs({
          company_id: params.company_id,
          service_date: params.service_date,
          service_time: params.service_time,
          exclude_service_id: params.exclude_service_id,
          service_code: params.service_code,
        })}`,
        setFetching: (v) => setFlag("sameTimeServices", v),
        signal,
        ...events,
      } as ServiceParams),
    [doGet, setFlag]
  );

  const list = useCallback(
    (
      filters?: Record<string, any>,
      events: Events = {},
      signal?: AbortSignal
    ) =>
      doGet({
        url: `${EndPoints.SERVICES.LIST}${qs(filters) ? `?${qs(filters)}` : ""}`,
        setFetching: (v) => setFlag("list", v),
        signal,
        ...events,
      } as ServiceParams),
    [doGet, setFlag]
  );

  const filterPassengers = useCallback(
    (
      filters?: Record<string, any>,
      events: Events = {},
      signal?: AbortSignal
    ) =>
      doGet({
        url: `${EndPoints.SERVICES.FILTER_PASSENGERS}${qs(filters) ? `?${qs(filters)}` : ""}`,
        setFetching: (v) => setFlag("filterPassengers", v),
        signal,
        ...events,
      } as ServiceParams),
    [doGet, setFlag]
  );

  // ✏️ EDITAR servicio (opcional)
  const edit = useCallback(
    (id: number, events: Events = {}) =>
      doGet({
        url: EndPoints.SERVICES.EDIT.replace(':id', String(id)),
        setFetching: v => setFlag('edit', v),
        ...events,
      } as ServiceParams),
    [doGet, setFlag]
  );

  // 📋 DUPLICAR servicio (1 día o rango service_date → service_date_to)
  const duplicate = useCallback(
    (
      id: number,
      payload: {
        service_date: string;
        service_date_to?: string;
        service_time: string;
        change_destination?: boolean;
        destination_id?: number;
        change_origin?: boolean;
        origin_id?: number;
      },
      events: Events = {}
    ) =>
      doPost({
        url: EndPoints.SERVICES.DUPLICATE.replace(':id', String(id)),
        body: payload,
        setFetching: v => setFlag('duplicate', v),
        ...events,
      } as ServiceParams),
    [doPost, setFlag]
  );

  // ↩️ RETORNO del servicio
  const retorno = useCallback(
    (
      id: number,
      payload: {
        service_date: string;
        service_time: string;
        change_destination?: boolean;
        destination_id?: number;
        change_origin?: boolean;
        origin_id?: number;
        enaex_code?: string;
      },
      events: Events = {}
    ) =>
      doPost({
        url: EndPoints.SERVICES.RETURN.replace(':id', String(id)),
        body: payload,
        setFetching: v => setFlag('retorno', v),
        ...events,
      } as ServiceParams),
    [doPost, setFlag]
  );

  // 🗑️ ELIMINAR servicio
  const remove = useCallback(
    (id: number, events: Events = {}) =>
      doDelete({
        url: EndPoints.SERVICES.DELETE.replace(':id', String(id)),
        setFetching: v => setFlag('delete', v),
        ...events,
      } as ServiceParams),
    [doDelete, setFlag]
  );

  // 📜 HISTORIAL de cambios del servicio
  const getHistory = useCallback(
    (serviceId: number, events: Events = {}) =>
      doGet({
        url: EndPoints.SERVICES.HISTORY.replace(':service_id', String(serviceId)),
        setFetching: v => setFlag('history', v),
        ...events,
      } as ServiceParams),
    [doGet, setFlag]
  );

  // 💰 PREVIEW de estado de pago
  const getPaymentStatusPreview = useCallback(
    (companyId: number, startDate: string, endDate: string, events: Events = {}) =>
      doGet({
        url: `${EndPoints.SERVICES.PAYMENT_STATUS}?company_id=${companyId}&start_date=${startDate}&end_date=${endDate}&action=preview`,
        setFetching: v => setFlag('paymentStatusPreview', v),
        ...events,
      } as ServiceParams),
    [doGet, setFlag]
  );

  const generateBoletaFromService = useCallback(
    (serviceId: number, events: Events = {}) =>
      doPost({
        url: EndPoints.SERVICES.BOLETA_GENERATE_FROM_SERVICE,
        body: { serviceId: String(serviceId) },
        setFetching: v => setFlag('generateBoletaFromService', v),
        ...events,
      } as ServiceParams),
    [doPost, setFlag]
  );

  const persistBoleta = useCallback(
    (boletaId: number, payload: Record<string, any> = {}, events: Events = {}) =>
      doPost({
        url: EndPoints.SERVICES.BOLETA_PERSIST.replace(':id', String(boletaId)),
        body: payload,
        setFetching: v => setFlag('persistBoleta', v),
        ...events,
      } as ServiceParams),
    [doPost, setFlag]
  );

  // 💰 DESCARGAR estado de pago
  const downloadPaymentStatus = useCallback(
    async (companyId: number, startDate: string, endDate: string, events: Events = {}) => {
      setFlag('paymentStatusDownload', true);
      try {
        const HOST = process.env.REACT_APP_API_HOST || 'http://localhost:8000';
        const API_VERSION = 'api/v1';
        // Usar el nuevo endpoint de download
        const url = `${HOST}/${API_VERSION}/services/payment-status/download/?company_id=${companyId}&start_date=${startDate}&end_date=${endDate}`;
        
        const headers: Record<string, string> = {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, */*',
        };
        
        const response = await fetchWithAuth(url, { method: 'GET', headers });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          events.onError?.({ 
            message: errorData.message || 'Error al descargar el archivo',
            http_status: response.status 
          });
          return;
        }
        
        const blob = await response.blob();
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `estado_pago_${companyId}_${startDate}_${endDate}.xls`;
        
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }
        
        const url_blob = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url_blob;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url_blob);
        
        events.onSuccess?.({ message: 'Archivo descargado correctamente' });
      } catch (error: any) {
        events.onError?.({ message: error?.message || 'Error al descargar el archivo' });
      } finally {
        setFlag('paymentStatusDownload', false);
      }
    },
    [setFlag]
  );

  const downloadServicesPlanilla = useCallback(
    async (
      companyId: number | 'all',
      startDate: string,
      endDate: string,
      events: Events = {}
    ) => {
      setFlag('servicesPlanillaDownload', true);
      try {
        const HOST = process.env.REACT_APP_API_HOST || 'http://localhost:8000';
        const API_VERSION = 'api/v1';
        const companyParam =
          companyId === 'all' ? 'all' : encodeURIComponent(String(companyId));
        const url = `${HOST}/${API_VERSION}/services/planilla/download/?company_id=${companyParam}&start_date=${startDate}&end_date=${endDate}`;

        const headers: Record<string, string> = {
          Accept:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, */*',
        };

        const response = await fetchWithAuth(url, { method: 'GET', headers });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          events.onError?.({
            message: errorData.message || 'Error al descargar la planilla',
            http_status: response.status,
          });
          return;
        }

        const blob = await response.blob();
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename =
          companyId === 'all'
            ? `planilla_servicios_completo_${startDate}_${endDate}.xlsx`
            : `planilla_servicios_${companyId}_${startDate}_${endDate}.xlsx`;

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        const urlBlob = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = urlBlob;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(urlBlob);

        events.onSuccess?.({ message: 'Planilla descargada correctamente' });
      } catch (error: any) {
        events.onError?.({ message: error?.message || 'Error al descargar la planilla' });
      } finally {
        setFlag('servicesPlanillaDownload', false);
      }
    },
    [setFlag]
  );

  const parseElAbraExcel = useCallback(
    (file: File, events: Events = {}) =>
      doPost({
        url: EndPoints.SERVICES.PARSE_EL_ABRA_EXCEL,
        body: { file },
        requestType: 'form',
        setFetching: (v) => setFlag('parseElAbraExcel', v),
        ...events,
      } as ServiceParams),
    [doPost, setFlag]
  );

  const convertImageToExcel = useCallback(
    async (file: File, events: Events = {}) => {
      setFlag('imageToExcel', true);
      try {
        const formData = new FormData();
        formData.append('file', file);

        const headers: Record<string, string> = {
          Accept:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/json, */*',
        };

        const response = await fetchWithAuth(EndPoints.SERVICES.IMAGE_TO_EXCEL, {
          method: 'POST',
          headers,
          body: formData,
        });

        const contentType = response.headers.get('Content-Type') || '';
        if (!response.ok) {
          const errorData = contentType.includes('json')
            ? await response.json().catch(() => ({}))
            : {};
          const errors = errorData?.errors;
          const pickError = (value: unknown): string | null => {
            if (typeof value === 'string' && value.trim()) return value.trim();
            if (Array.isArray(value) && value.length > 0) return String(value[0]);
            return null;
          };
          const msg =
            pickError(errors?.extraction) ||
            pickError(errors?.file) ||
            pickError(errors?.excel) ||
            (errors && typeof errors === 'object'
              ? pickError(Object.values(errors)[0])
              : null) ||
            pickError(errorData?.message) ||
            'No se pudo convertir la imagen a Excel';
          events.onError?.({ message: msg, http_status: response.status });
          return;
        }

        const blob = await response.blob();
        const disposition = response.headers.get('Content-Disposition');
        let filename = `planilla_el_abra_${Date.now()}.xlsx`;
        if (disposition) {
          const match = disposition.match(/filename="?([^";]+)"?/i);
          if (match?.[1]) filename = match[1];
        }

        const urlBlob = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = urlBlob;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(urlBlob);

        events.onSuccess?.({
          message: 'Excel generado correctamente',
          data: {
            block_count: response.headers.get('X-Block-Count'),
            passenger_count: response.headers.get('X-Passenger-Count'),
          },
        });
      } catch (error: any) {
        events.onError?.({ message: error?.message || 'Error de red al convertir la imagen' });
      } finally {
        setFlag('imageToExcel', false);
      }
    },
    [setFlag]
  );

  return {
    fetching,
    sameTimeServices,
    step1,
    step2,
    step3,
    store,
    duplicate,
    retorno,
    list,
    filterPassengers,
    edit,
    remove,
    update,
  updateRoute,
    getRoute,
    updateStatus,
    assignDriver,
    releaseDriver,
    updateAdicional,
    getById,
    getHistory,
    getPaymentStatusPreview,
    downloadPaymentStatus,
    downloadServicesPlanilla,
    parseElAbraExcel,
    convertImageToExcel,
    generateBoletaFromService,
    persistBoleta,
  };
};

export default useServicesService;
